import { Fragment, useCallback, useEffect, useState } from 'react'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { Icon } from '../../components/icons/Icon'
import { listExternalAttestations } from '../../api/attestation'
import { downloadEvidenceFile, getEvidenceFileMeta, listEvidenceItems, promoteEvidenceItem } from '../../api/evidence'
import { ApiError } from '../../api/problem'
import { label } from '../../lib/labels'
import { EvidenceRequirementsSection } from './EvidenceRequirementsSection'
import { EvidenceUploadForm } from './EvidenceUploadForm'
import type { ExternalAttestationView } from '../../types/attestation'
import type { EvidenceFileView, EvidenceItemView, EvidenceUploadResult } from '../../types/evidence'
import type { ProgramFilingDetailView } from '../../types/programFilingDetail'

export interface EvidencePanelProps {
  caseId: string
  /** Optional — attestation linkage (below) only resolves once this case's filings are known.
   * `CaseDetailScreen` always has them in hand by the time this tab renders; the prop stays
   * optional so this panel still degrades gracefully (no linkage column, never a crash) if a
   * future caller renders it standalone. Also drives the per-filing requirement + finalize-
   * readiness block (evidence-vault story, FR-M14.3/4/5). */
  filings?: ProgramFilingDetailView[]
}

/** A stable (module-level, never-recreated) empty array — the default for the optional `filings`
 * prop. A fresh `[]` literal as a default parameter value is recreated on every render, which
 * would defeat the attestation-linkage effect's `[filings]` dependency (a new array reference every
 * render re-runs the effect every render, an infinite `setState`-triggers-render loop when no
 * `filings` prop is passed at all). This constant is the fix — same reference every render. */
const EMPTY_FILINGS: ProgramFilingDetailView[] = []

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

const ATTESTATION_STATUS_VARIANT: Record<string, 'success' | 'info' | 'warning' | 'danger' | 'neutral' | 'muted'> = {
  draft: 'neutral',
  uploaded: 'info',
  signed: 'success',
  rejected: 'danger',
  withdrawn: 'muted',
}

const ITEM_STATUS_VARIANT: Record<string, StatusPillVariant> = {
  draft: 'info',
  accepted: 'success',
  quarantined: 'danger',
}

const AV_SCAN_VARIANT: Record<string, StatusPillVariant> = {
  clean: 'success',
  infected: 'danger',
  pending: 'neutral',
}

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Every `ExternalAttestation` whose `reportRef` resolves to this evidence item (case-enrichment
 * story) — `ExternalAttestation.reportRef` is validated same-tenant/same-Case at creation
 * (`ExternalAttestationService.resolveReportRef`) against an `EvidenceItem.id`, so this is the real
 * backing linkage, never a fabricated join. An evidence item nothing yet attests to renders an
 * honest "not yet linked" line, not an empty table.
 */
function AttestationLinkage({ attestations }: { attestations: ExternalAttestationView[] }) {
  if (attestations.length === 0) {
    return <p style={{ margin: 0, color: 'var(--iw-text-muted)' }}>Not yet linked to any attestation.</p>
  }
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
      {attestations.map((a) => (
        <li key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
          <strong>{label('attestationType', a.type)}</strong>
          <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>by {a.attestor}</span>
          <StatusPill variant={ATTESTATION_STATUS_VARIANT[a.status] ?? 'neutral'} label={label('attestationStatus', a.status)} />
          <span style={{ color: 'var(--iw-text-subtle)', fontSize: 'var(--iw-size-xs)' }}>{a.date}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * One item's file/AV-scan cell (evidence-vault story, FR-M14.2) — fetches `GET .../file-meta`
 * lazily per row (a metadata-only, direct-create item legitimately 404s; that is rendered as an
 * honest "Metadata only" pill, never a spinner stuck forever or a fabricated "clean" scan). A
 * `clean`/`accepted` (or still-`draft`) item gets a real Download action (`GET .../file`, byte-
 * exact, saved via `Blob`); a `quarantined` item never does — `EvidenceItemService.downloadFile`
 * itself refuses it 409, so no button is rendered for it at all rather than a dead one. The
 * `CleanMalwareScanStub` behind this build always reports `clean` — shown honestly as whatever the
 * server actually returns, not hard-coded here.
 */
function EvidenceFileCell({ item, onPromoted }: { item: EvidenceItemView; onPromoted: (updated: EvidenceItemView) => void }) {
  const [file, setFile] = useState<EvidenceFileView | 'none' | 'loading' | 'error'>('loading')
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setFile('loading')
    getEvidenceFileMeta(item.id)
      .then((meta) => {
        if (!cancelled) setFile(meta)
      })
      .catch((err) => {
        if (cancelled) return
        setFile(err instanceof ApiError && err.status === 404 ? 'none' : 'error')
      })
    return () => {
      cancelled = true
    }
  }, [item.id])

  async function handleDownload() {
    setBusy(true)
    setActionError(null)
    try {
      const { blob, filename } = await downloadEvidenceFile(item.id)
      saveBlob(blob, filename)
    } catch (err) {
      setActionError(errorMessage(err, 'Could not download this file.'))
    } finally {
      setBusy(false)
    }
  }

  async function handlePromote() {
    setBusy(true)
    setActionError(null)
    try {
      const updated = await promoteEvidenceItem(item.id)
      onPromoted(updated)
    } catch (err) {
      setActionError(errorMessage(err, 'Could not promote this evidence item.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
        {file === 'loading' ? <span style={{ color: 'var(--iw-text-subtle)', fontSize: 'var(--iw-size-xs)' }}>Loading…</span> : null}
        {file === 'none' ? <StatusPill variant="muted" label="Metadata only" /> : null}
        {file === 'error' ? <StatusPill variant="warning" label="Scan status unavailable" /> : null}
        {file !== 'loading' && file !== 'none' && file !== 'error' ? (
          <StatusPill
            variant={AV_SCAN_VARIANT[file.avScanStatus] ?? 'neutral'}
            label={`AV: ${label('avScanStatus', file.avScanStatus)}`}
          />
        ) : null}
        {file !== 'loading' && file !== 'none' && file !== 'error' && item.status !== 'quarantined' ? (
          <button type="button" className="iw-btn iw-btn--sm" disabled={busy} onClick={() => void handleDownload()}>
            <Icon name="download" size={12} /> {busy ? '…' : 'Download'}
          </button>
        ) : null}
        {item.status === 'draft' ? (
          <button type="button" className="iw-btn iw-btn--sm iw-btn--primary" disabled={busy} onClick={() => void handlePromote()}>
            {busy ? '…' : 'Promote'}
          </button>
        ) : null}
      </div>
      {actionError ? <span style={{ color: 'var(--iw-danger)', fontSize: 'var(--iw-size-xs)' }}>{actionError}</span> : null}
    </div>
  )
}

/**
 * The Case-workspace Evidence tab (evidence-vault story, IB-167/M14, extending the earlier
 * read-only IB-16 tab): a real multipart upload (`EvidenceUploadForm`) into the object-store
 * vault, per-item lifecycle (draft/accepted/quarantined) and AV-scan status
 * (`EvidenceFileCell`, `GET .../file-meta`), a real byte-exact Download (`GET .../file`), and —
 * per `ProgramFiling` on this Case — the real evidence-requirement list, satisfy/waive actions,
 * and the real FR-M14.5 finalize-readiness gate (`EvidenceRequirementsSection`). Each evidence
 * item row still expands to the `ExternalAttestation`(s) that name it as their `reportRef` (`GET
 * .../external-attestations`, already published, unchanged from the original IB-16 tab).
 */
export function EvidencePanel({ caseId, filings = EMPTY_FILINGS }: EvidencePanelProps) {
  const [rows, setRows] = useState<EvidenceItemView[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [attestationsByReportRef, setAttestationsByReportRef] = useState<Map<string, ExternalAttestationView[]>>(
    new Map(),
  )

  const load = useCallback(() => {
    setState('loading')
    setError(null)
    listEvidenceItems(caseId)
      .then((page) => {
        setRows(page.items)
        setState('ready')
      })
      .catch((err) => {
        setError(errorMessage(err, 'Could not load evidence items.'))
        setState('error')
      })
  }, [caseId])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    let cancelled = false
    if (filings.length === 0) {
      setAttestationsByReportRef(new Map())
      return
    }
    Promise.all(filings.map((f) => listExternalAttestations(f.id).catch(() => ({ items: [], nextCursor: null }))))
      .then((pages) => {
        if (cancelled) return
        const byReportRef = new Map<string, ExternalAttestationView[]>()
        for (const page of pages) {
          for (const attestation of page.items) {
            const existing = byReportRef.get(attestation.reportRef) ?? []
            byReportRef.set(attestation.reportRef, [...existing, attestation])
          }
        }
        setAttestationsByReportRef(byReportRef)
      })
      .catch(() => {
        if (!cancelled) setAttestationsByReportRef(new Map())
      })
    return () => {
      cancelled = true
    }
    // `filings` is an array prop from the caller's own state — identity-stable enough in practice
    // (CaseDetailScreen fetches it once per case load); re-running on every render would be
    // wasteful but not incorrect, since this effect is idempotent and cheap to re-derive.
  }, [filings])

  function handleUploaded(result: EvidenceUploadResult) {
    setRows((prev) => [result.item, ...prev])
  }

  function handlePromoted(updated: EvidenceItemView) {
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
  }

  const acceptedItems = rows.filter((r) => r.status === 'accepted')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-5)' }}>
      <EvidenceUploadForm caseId={caseId} onUploaded={handleUploaded} />

      {state === 'loading' ? <p style={{ color: 'var(--iw-text-muted)' }}>Loading evidence…</p> : null}

      {state === 'error' ? (
        <InlineAlert
          severity="error"
          action={
            <button type="button" className="iw-btn iw-btn--sm" onClick={load}>
              Try again
            </button>
          }
        >
          {error}
        </InlineAlert>
      ) : null}

      {state === 'ready' && rows.length === 0 ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>No evidence items recorded yet for this case.</p>
      ) : null}

      {state === 'ready' && rows.length > 0 ? (
        <table className="iw-table" aria-label="Evidence items">
          <thead>
            <tr>
              <th scope="col">Type</th>
              <th scope="col">Status</th>
              <th scope="col">Contemporaneous</th>
              <th scope="col">Retention policy</th>
              <th scope="col">Region</th>
              <th scope="col">Hash</th>
              <th scope="col">Recorded</th>
              <th scope="col">File / AV scan</th>
              <th scope="col">Attestations</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const expanded = expandedId === row.id
              const linked = attestationsByReportRef.get(row.id) ?? []
              return (
                <Fragment key={row.id}>
                  <tr>
                    <td>{label('evidenceType', row.type)}</td>
                    <td>
                      <StatusPill
                        variant={ITEM_STATUS_VARIANT[row.status] ?? 'neutral'}
                        label={label('evidenceItemStatus', row.status)}
                      />
                    </td>
                    <td>
                      <StatusPill
                        variant={row.contemporaneous ? 'success' : 'neutral'}
                        label={row.contemporaneous ? 'Contemporaneous' : 'Not contemporaneous'}
                      />
                    </td>
                    <td>{row.retentionPolicy}</td>
                    <td>{row.region}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 'var(--iw-size-xs)', wordBreak: 'break-all' }}>
                      {row.hash}
                    </td>
                    <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                      {formatDate(row.createdAt)}
                    </td>
                    <td>
                      <EvidenceFileCell item={row} onPromoted={handlePromoted} />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="iw-btn iw-btn--sm"
                        aria-expanded={expanded}
                        aria-label={`Attestations for ${label('evidenceType', row.type)} evidence item`}
                        onClick={() => setExpandedId(expanded ? null : row.id)}
                      >
                        {expanded ? 'Hide' : linked.length > 0 ? `${linked.length} linked` : 'None linked'}
                      </button>
                    </td>
                  </tr>
                  {expanded ? (
                    <tr>
                      <td colSpan={9}>
                        <AttestationLinkage attestations={linked} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      ) : null}

      {filings.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
          <h3 style={{ margin: 0 }}>Requirements &amp; finalize readiness</h3>
          {filings.map((filing) => (
            <EvidenceRequirementsSection key={filing.id} filing={filing} acceptedItems={acceptedItems} />
          ))}
        </div>
      ) : null}
    </div>
  )
}
